/* global console */
import { createSlice, current } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { applyStylesheet } from "./cellStylesSlice";

function applyBorderPresetChange(state, payload) {
  if (payload.name) {
    state
      .filter((b) => b.preset === payload.name)
      .forEach((b) => {
        b.color = payload.color;
        b.weight = payload.width;
        b.style = payload.type;
      });
  }
}

const bordersSlice = createSlice({
  name: "borders",
  initialState: [],
  reducers: {
    reset: (state) => {
      if (state.length > 0) {
        state.splice(0, state.length);
      }
    },
    init: (state, action) => {
      if (action.payload) {
        if (state.length > 0) {
          state.splice(0, state.length);
        }
        state.push(...action.payload);
      }
    },
    add: (state, action) => {
      //console.log(action);

      // TODO: find first from - to where no border exists
      const sameLocationBorders = state.filter(
        (b) =>
          b.isVertical === (action.payload.columnIndex !== undefined) &&
          b.index === (action.payload.columnIndex ?? action.payload.rowIndex)
      ); // finding all borders that are in same location
      if (sameLocationBorders?.length > 0) {
        const lowestFrom = sameLocationBorders.reduce((r, n) => (n.from < r ? n.from : r), Number.MAX_SAFE_INTEGER);
        //console.log("Same location borders", { sameLocationBorders, state, action, lowestFrom });
        if (lowestFrom >= 1 && lowestFrom < Number.MAX_SAFE_INTEGER) {
          state.push({
            id: action.payload.id ?? uuidv4(),
            index: action.payload.columnIndex ?? action.payload.rowIndex,
            isVertical: action.payload.columnIndex !== undefined,
            weight: action.payload.weight ?? 1,
            color: action.payload.color ?? "black",
            style: action.payload.style ?? "solid",
            preset: action.payload.preset,
            from: action.payload.from ?? 0,
            to: action.payload.to ?? lowestFrom,
          });
        }
      } else {
        state.push({
          id: action.payload.id ?? uuidv4(),
          index: action.payload.columnIndex ?? action.payload.rowIndex,
          isVertical: action.payload.columnIndex !== undefined,
          weight: action.payload.weight ?? 1,
          color: action.payload.color ?? "black",
          style: action.payload.style ?? "solid",
          preset: action.payload.preset,
          from: action.payload.from ?? 0,
          to: action.payload.to ?? null,
        });
      }
      //console.log(current(state));
    },
    remove: (state, action) => {
      console.log(action);
      if (action.payload?.id) {
        const index = state.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.splice(index, 1);
        }
      }

      if (action.payload) {
        const index = state.findIndex(
          (b) => b.isVertical === action.payload.isVertical && b.index === action.payload.index
        );
        if (index !== -1) {
          state.splice(index, 1);
        }
      }
      console.log(current(state));
    },
    onChange: (state, action) => {
      console.log("on change border update", { ...action.payload });
      if (action.payload.columnIndexRemoved !== undefined) {
        for (let i = 0; i < state.length; i++) {
          const b = state[i];
          if (b.isVertical && b.index > action.payload.columnIndexRemoved) {
            b.index = b.index - 1;
          }
        }
      }

      if (action.payload.rowIndexRemoved !== undefined) {
        for (let i = 0; i < state.length; i++) {
          const b = state[i];
          if (!b.isVertical && b.index > action.payload.rowIndexRemoved) {
            b.index = b.index - 1;
          }
        }
      }

      if (action.payload.columnIndexAdded !== undefined) {
        let newBorder = null;
        for (let i = 0; i < state.length; i++) {
          const b = state[i];
          if (b.isVertical && b.index > action.payload.columnIndexAdded) {
            b.index = b.index + 1;
          }

          if (b.isVertical && b.index === action.payload.columnIndexAdded) {
            newBorder = { ...b, index: action.payload.columnIndexAdded + 1 };
          }
        }
        if (newBorder) state.push(newBorder);
      }

      if (action.payload.rowIndexAdded !== undefined) {
        let newBorder = null;
        for (let i = 0; i < state.length; i++) {
          const b = state[i];
          if (!b.isVertical && b.index > action.payload.rowIndexAdded) {
            b.index = b.index + 1;
          }

          if (!b.isVertical && b.index === action.payload.rowIndexAdded) {
            newBorder = { ...b, index: action.payload.rowIndexAdded + 1 };
          }
        }
        if (newBorder) state.push(newBorder);
      }
      console.log("borders updated", { current: current(state), payload: action.payload });
    },
    update: (state, action) => {
      console.log(action);
      if (action.payload) {
        const f = state.find((b) => b.id === action.payload.id);
        f.weight = action.payload.weight ?? f.weight;
        f.color = action.payload.color ?? f.color;
        f.style = action.payload.style ?? f.style;
        f.from = action.payload.from ?? f.from;
        f.to = action.payload.to ?? f.to;
        f.preset = action.payload.preset;
        if (f.from >= (f.to ?? Number.MAX_VALUE)) {
          f.from = f.to - 1;
          if (f.from < 0) {
            f.from = 0;
            f.to = null;
          }
        }
      }
      console.log(current(state));
    },
    presetChanged: (state, action) => {
      applyBorderPresetChange(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(applyStylesheet, (state, action) => {
      const sheet = action.payload;
      if (!sheet) {
        return;
      }
      sheet.borders?.forEach((b) => {
        applyBorderPresetChange(state, {
          ...b,
          color: sheet.colors?.find((c) => c.name === b.color)?.value ?? "black",
        });
      });
    });
  },
});

export const { reset, init, add, remove, onChange, update, presetChanged } = bordersSlice.actions;

export default bordersSlice.reducer;
